export type ContentTypeEventHandler = () => void;

export type ContentTypeCommandHandler = (message: ContentTypeCommand) => void;

export type ContentTypeCommandType = 'copy' | 'analytics';

const ProductKeys = ['whiteboards', 'databases', 'slides', 'confluence'] as const;
export type ContentTypeProductKey = (typeof ProductKeys)[number];

// CQRS
export type ContentTypeCommand = {
	/**
	 * Type of the command being triggered, helps us locate an appropriate handler
	 */
	type: ContentTypeCommandType;
	/**
	 * Source of the command, helps us trace back where the event originated from
	 */
	source: ContentTypeProductKey;
	/**
	 * Payload of the command, contains any additional data that needs to be passed along
	 */
	payload?: any;
};

// Shared service that currently holds methods for whiteboards version history
// This can be extended to support databases related work
export class ContentTypeEventService {
	private eventHandlers: Map<string, ContentTypeEventHandler>;

	// Will eventually completely replace V1 event handlers
	private eventHandlersByProduct: Map<
		ContentTypeProductKey,
		Map<string, ContentTypeCommandHandler>
	>;

	constructor() {
		this.eventHandlers = new Map();

		this.eventHandlersByProduct = new Map();
		this.eventHandlersByProduct.set('whiteboards', new Map());
		this.eventHandlersByProduct.set('databases', new Map());
		this.eventHandlersByProduct.set('slides', new Map());
		this.eventHandlersByProduct.set('confluence', new Map());
	}

	dispose() {
		// Using clear here instead of re-assignment to a new map to save memory.
		this.eventHandlers.clear();
		this.eventHandlersByProduct.clear();
	}

	/**
	 * This is called in Whiteboards to register the any event callbacks.
	 * Confluence FE can then use any of these events to trigger the whiteboard events.
	 */
	registerEventHandler(event: string, handler: ContentTypeEventHandler): void {
		this.eventHandlers.set(event, handler);
	}

	/**
	 * Setup function to be called by a product to register event callbacks. When you use this, you can
	 * listen to certain events from other products and NOTIFY other products about certain events
	 */
	mountEventService(source: ContentTypeProductKey) {
		/**
		 * Expected usage in downstream product:
		 * const eventService = contentTypesEventService.mountEventService('whiteboards');
		 * eventService.registerHandler('copy', (message) => {
		 *   // Handle the copy event
		 *   console.log('Whiteboards has been notified about a copy', message);
		 * });
		 *
		 * Invoking a command from another product (e.g., Confluence):
		 * const confluenceEventService = contentTypesEventService.mountEventService('confluence');
		 * confluenceEventService.sendCommand({
		 *  type: 'copy',
		 * });
		 */
		return {
			/**
			 * Called by whatever product to mount a listener that will only trigger when OTHER products
			 * invoke a command that matches the ContentTypeCommandType.
			 */
			registerHandler: (event: ContentTypeCommandType, handler: ContentTypeCommandHandler) => {
				this.eventHandlersByProduct.get(source)?.set(event, handler);
			},

			/**
			 * Called by whatever product to unregister a listener that was previously registered
			 */
			unregisterHandler: (event: ContentTypeCommandType) => {
				this.eventHandlersByProduct.get(source)?.delete(event);
			},

			/**
			 * Check if an event has any handlers registered to it
			 * @param event
			 */
			hasEventHandler: (event: ContentTypeCommandType) => {
				return this.eventHandlersByProduct.get(source)?.has(event);
			},

			/**
			 * Trigger an event that OTHER products will receive
			 */
			sendCommand: (message: Omit<ContentTypeCommand, 'source'>) => {
				if (!message || !message.type) {
					return;
				}

				// Call all other sources and notify them that we have triggered an event
				ProductKeys.forEach((productKey) => {
					// Exclude self. NO SELF TRIGGERING!
					if (productKey === source) {
						return;
					}

					this.eventHandlersByProduct.get(productKey)?.get(message.type)?.({
						...message.payload,
						source, // Source gets injected automatically after we have mounted it once.
					});
				});
			},
		};
	}

	/**
	 * This is called in Whiteboards to unregister the any event callbacks.
	 */
	unregisterEventHandler(event: string): void {
		this.eventHandlers.delete(event);
	}

	/**
	 * Check if an event has any handlers registered to it
	 * @param event
	 */
	hasEventHandler(event: string): boolean {
		return this.eventHandlers.has(event);
	}

	/**
	 * Trigger an event in Whiteboards.
	 */
	triggerEvent(event: string): void {
		this.eventHandlers.get(event)?.();
	}
}
